import { useCallback, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import FormFieldVersionInput from "components/FormFields/VersionInput";
import VersionTooltipContent from "components/FormFields/VersionInput/VersionTooltipContent";
import { VersionInputOption } from "components/FormFields/VersionInput/types";
import FormFooterEdit from "components/Forms/FooterEdit";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import { OPENTOFU_WORKFLOW_TOOL_OPTIONS } from "constants/opentofuWorkflowTools";
import { VENDOR_DICTIONARY, VENDOR_KEY } from "constants/vendor";
import FormField from "ds/components/Form/Field";
import FormToggleField from "ds/components/Form/ToggleField";
import Input from "ds/components/Input";
import Select from "ds/components/Select";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useTypedContext from "hooks/useTypedContext";
import useGetVendorResources from "shared/Stack/Vendor/useGetVendorResources";
import { OpenTofuWorkflowTool } from "types/generated";
import { stackValidator } from "utils/formValidators";
import { getDocsUrl } from "utils/getDocsUrl";

import { StackSettingsContextData } from "../../Context";
import StackSettingsViewVendorFormField from "../../components/ViewVendorFormField";
import { StackSettingsVendorComponentBaseProps } from "../types";
import {
  getConciseTooltip,
  getEnableCPUProfilingTooltip,
  getExternalStateAccessEnabledTooltip,
  getWorkflowToolTooltip,
} from "./getTooltips";
import {
  getFormDefaultValues,
  getValidateToolVersion,
  mapCreateStackVendorUpdateInput,
} from "./helpers";

export type StackSettingsOpenTofuVendorFormFields = {
  version: VersionInputOption;
  useSmartSanitization: boolean;
  workflowTool: OpenTofuWorkflowTool;
  externalStateAccessEnabled: boolean;
  workspace: string;
  concise: boolean;
  enableCPUProfiling: boolean;
};

type StackSettingsVendorOpenTofuProps = StackSettingsVendorComponentBaseProps;

const StackSettingsVendorOpenTofuEdit = ({
  onStackUpdate,
  updateLoading,
}: StackSettingsVendorOpenTofuProps) => {
  const { openTofuVersions, isRefetching, refetch } = useGetVendorResources({
    vendorSpecific: VENDOR_KEY.OPEN_TOFU,
  });

  const { stackSettings } = useTypedContext(StackSettingsContextData);

  const openTofuVendorForm = useForm<StackSettingsOpenTofuVendorFormFields>({
    defaultValues: getFormDefaultValues(stackSettings),
    mode: "onChange",
  });

  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { isDirty, errors },
    setValue,
    watch,
    trigger,
  } = openTofuVendorForm;

  useBlockNavigationModal({ isNavigationBlocked: isDirty });

  const watchedWorkflow = watch("workflowTool");
  const watchedVersion = watch("version");

  const isVersionInputEnabled = watchedWorkflow !== OpenTofuWorkflowTool.Custom;

  const handleWorkflowToolChange = useCallback(
    (onFieldChange: (value: OpenTofuWorkflowTool) => void) => (value: OpenTofuWorkflowTool) => {
      if (watchedWorkflow === value) {
        return undefined;
      }

      const version =
        value === OpenTofuWorkflowTool.Opentofu && openTofuVersions ? openTofuVersions[0] : "";

      onFieldChange(value);
      setValue("version", {
        value: version,
        type: watchedVersion.type,
      });

      trigger();
    },
    [openTofuVersions, setValue, trigger, watchedVersion.type, watchedWorkflow]
  );

  const onSubmit = (formData: StackSettingsOpenTofuVendorFormFields) => {
    onStackUpdate(mapCreateStackVendorUpdateInput(formData));
  };

  useEffect(() => {
    reset(getFormDefaultValues(stackSettings));
  }, [reset, stackSettings]);

  return (
    <>
      <StackSettingsViewVendorFormField vendorName={VENDOR_DICTIONARY.OPEN_TOFU} />
      <Controller
        name="workflowTool"
        control={control}
        render={({ field, fieldState }) => (
          <FormField
            error={fieldState.error?.message}
            noMargin
            label="Workflow tool"
            {...getWorkflowToolTooltip()}
          >
            {({ ariaInputProps }) => (
              <Select
                value={field.value}
                items={OPENTOFU_WORKFLOW_TOOL_OPTIONS}
                onChange={handleWorkflowToolChange(field.onChange)}
                isError={!!fieldState.error}
                {...ariaInputProps}
              />
            )}
          </FormField>
        )}
      />
      {isVersionInputEnabled && (
        <Controller
          name="version"
          control={control}
          rules={{
            validate: getValidateToolVersion(openTofuVersions),
          }}
          render={({ field, fieldState }) => {
            const version = field.value;

            return (
              <FormFieldVersionInput
                title={`${VENDOR_DICTIONARY.OPEN_TOFU} version`}
                tooltipInfo={
                  <>
                    <TooltipModalTitle>Choose version</TooltipModalTitle>
                    <TooltipModalBody align="start">
                      <VersionTooltipContent />
                      <ReadMoreDocsLink
                        docsUrl={getDocsUrl(
                          "/concepts/stack/stack-settings#opentofuterraform-specific-settings"
                        )}
                      />
                    </TooltipModalBody>
                  </>
                }
                reloadLoading={isRefetching}
                reloadVersionsData={refetch}
                errorMessage={fieldState.error?.message}
                type={version.type}
                version={version.value}
                supportedVersions={openTofuVersions}
                onChange={field.onChange}
              />
            );
          }}
        />
      )}
      <Controller
        name="useSmartSanitization"
        control={control}
        render={({ field }) => (
          <FormToggleField
            variant="switch"
            align="center"
            onChange={field.onChange}
            checked={field.value}
            title="Smart sanitization (recommended)"
            description='If set, Spacelift will attempt to sanitize the resources created by OpenTofu using the information provided by OpenTofu as to which fields are "sensitive" or not'
          />
        )}
      />
      <Controller
        name="concise"
        control={control}
        render={({ field }) => (
          <FormToggleField
            variant="switch"
            align="center"
            onChange={field.onChange}
            checked={field.value}
            title="Concise output"
            description='Suppresses the "Refreshing state..." progress output during plan, apply and refresh'
            tooltipInfo={getConciseTooltip().tooltipInfo}
          />
        )}
      />
      <Controller
        name="enableCPUProfiling"
        control={control}
        render={({ field }) => (
          <FormToggleField
            variant="switch"
            align="center"
            onChange={field.onChange}
            checked={field.value}
            title="CPU profiling"
            description="Captures CPU profiles from OpenTofu init, plan, and apply phases"
            tooltipInfo={getEnableCPUProfilingTooltip().tooltipInfo}
          />
        )}
      />
      {stackSettings.managesStateFile && (
        <Controller
          name="externalStateAccessEnabled"
          control={control}
          render={({ field }) => (
            <FormToggleField
              variant="switch"
              align="center"
              onChange={field.onChange}
              checked={field.value}
              title="External state access"
              description="Enables external, read-only access to the state of this Stack"
              tooltipInfo={getExternalStateAccessEnabledTooltip().tooltipInfo}
            />
          )}
        />
      )}
      {!stackSettings.managesStateFile && (
        <FormField label="Workspace" error={errors?.workspace?.message} isOptional noMargin>
          {({ ariaInputProps }) => (
            <Input
              placeholder="OpenTofu workspace to select"
              {...register("workspace", {
                validate: (value) => stackValidator.validateWorkspaceField(value) || true,
              })}
              error={!!errors?.workspace?.message}
              {...ariaInputProps}
            />
          )}
        </FormField>
      )}
      <FormFooterEdit
        onSubmit={handleSubmit(onSubmit)}
        onCancel={() => reset(getFormDefaultValues(stackSettings))}
        isDirty={isDirty}
        submitLoading={updateLoading}
      />
    </>
  );
};

export default StackSettingsVendorOpenTofuEdit;
